import { createFileRoute, Link } from "@tanstack/react-router";
import { doc, updateDoc } from "firebase/firestore";
import { Inbox, Loader2, RotateCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { useConfirm } from "@/components/DialogProvider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { db } from "@/firebase";
import type { ProjectHours } from "@/firestore_types";
import { useAllProjectHours } from "@/features/projects/hooks/useProjectHours";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { formatTimeEST } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/admin/trash/project-hours")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectHours, loading } = useAllProjectHours({ includeDeleted: true });
  const { workers, loading: workersLoading } = useAllWorkers();
  const { projects, loading: projectsLoading } = useProjects();

  const workerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    workers.forEach((worker) => {
      map.set(worker.id, worker.name);
    });
    return map;
  }, [workers]);

  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach((project) => {
      map.set(project.id, project.name);
    });
    return map;
  }, [projects]);

  const deletedHours = useMemo(() => {
    const hours = projectHours.filter((hour) => hour.isDeleted === true);
    return [...hours].sort(
      (a, b) => b.clockedInAt.toMillis() - a.clockedInAt.toMillis(),
    );
  }, [projectHours]);

  const isLoading = loading || workersLoading || projectsLoading;

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    );
  }

  if (deletedHours.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col gap-4">
      {deletedHours.map((hour) => (
        <DeletedHourCard
          key={hour.id}
          hour={hour}
          workerName={workerNameMap.get(hour.workerId) ?? "Unknown worker"}
          projectName={projectNameMap.get(hour.projectId) ?? "Unknown project"}
        />
      ))}
    </div>
  );
}

interface DeletedHourCardProps {
  hour: ProjectHours;
  workerName: string;
  projectName: string;
}

function DeletedHourCard({ hour, workerName, projectName }: DeletedHourCardProps) {
  const clockInLabel = formatTimeEST(hour.clockedInAt, true) || "Unknown";
  const clockOutLabel = hour.clockedOutAt
    ? formatTimeEST(hour.clockedOutAt, true) || "Unknown"
    : "Not clocked out";
  const statusLabel = hour.clockedOut
    ? hour.approved
      ? "Approved"
      : "Pending approval"
    : "In progress";
  const entryType = hour.manualEntry ? "Manual entry" : "Clock-in";

  return (
    <Card className="border-muted/60">
      <CardHeader className="gap-4 md:flex md:items-center md:justify-between">
        <div className="space-y-1">
          <CardTitle className="text-lg font-semibold">
            <Link
              to="/dashboard/admin/workers/$workerId"
              params={{ workerId: hour.workerId }}
              className="cursor-pointer text-blue-600 transition-colors hover:text-blue-800 hover:underline"
            >
              {workerName}
            </Link>
          </CardTitle>
          <CardDescription>
            <Link
              to="/dashboard/admin/projects/$projectId"
              params={{ projectId: hour.projectId }}
              className="cursor-pointer text-blue-600 transition-colors hover:text-blue-800 hover:underline"
            >
              {projectName}
            </Link>
          </CardDescription>
        </div>
        <CardAction>
          <RestoreHourButton hourId={hour.id} />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <DetailRow label="Clocked in" value={clockInLabel} />
        <DetailRow label="Clocked out" value={clockOutLabel} />
        <DetailRow label="Status" value={statusLabel} />
        <DetailRow label="Entry type" value={entryType} />
      </CardContent>
    </Card>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="border-muted flex flex-col gap-1 rounded-lg border border-dashed p-4">
      <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
        {label}
      </span>
      <span className="text-foreground text-sm font-medium">{value}</span>
    </div>
  );
}

function RestoreHourButton({ hourId }: { hourId: string }) {
  const confirm = useConfirm();
  const [restoring, setRestoring] = useState(false);

  const handleRestore = async () => {
    const confirmed = await confirm({
      title: "Restore time entry?",
      description: "The time entry will return to all project hours views.",
      confirmText: "Restore",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    setRestoring(true);
    try {
      await updateDoc(doc(db, "ProjectHours", hourId), { isDeleted: false });
      toast.success("Time entry restored.");
    } catch (error) {
      console.error("Failed to restore time entry", error);
      toast.error("Failed to restore time entry. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Button onClick={handleRestore} size="sm" variant="outline" disabled={restoring}>
      {restoring ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Restoring...
        </>
      ) : (
        <>
          <RotateCcw className="size-4" />
          Restore
        </>
      )}
    </Button>
  );
}

function EmptyState() {
  return (
    <div className="border-muted bg-muted/30 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-8 py-16 text-center">
      <Inbox className="text-muted-foreground size-10" />
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">No deleted time entries</p>
        <p className="text-muted-foreground text-sm">
          Deleted project hours will appear here so you can restore them if needed.
        </p>
      </div>
    </div>
  );
}
